import { Dialog } from './FarChat/ChatFeedbackOverlays';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ChangelogRelease, fetchChangelog } from '../api/changelogFunctions';

interface ChangelogDialogProps {
  open: boolean;
  onClose: () => void;
}

function sortNewestFirst(releases: ChangelogRelease[]): ChangelogRelease[] {
  return [...releases].sort((a, b) => b.date.localeCompare(a.date));
}

function oneCalendarMonthAgo(today: Date): string {
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();
  const priorMonthLastDay = new Date(year, month, 0).getDate();
  const threshold = new Date(year, month - 1, Math.min(day, priorMonthLastDay));
  const thresholdMonth = String(threshold.getMonth() + 1).padStart(2, '0');
  const thresholdDay = String(threshold.getDate()).padStart(2, '0');
  return `${threshold.getFullYear()}-${thresholdMonth}-${thresholdDay}`;
}

function splitReleasesByAge(releases: ChangelogRelease[], today: Date) {
  const threshold = oneCalendarMonthAgo(today);
  return {
    recent: releases.filter((release) => release.date >= threshold),
    archive: releases.filter((release) => release.date < threshold),
  };
}

function formatReleaseDate(date: string): string {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'long' }).format(
    new Date(`${date}T12:00:00`)
  );
}

function safeExternalHref(href?: string): string | null {
  if (!href) return null;
  try {
    const url = new URL(href, window.location.origin);
    return ['http:', 'https:', 'mailto:'].includes(url.protocol) ? href : null;
  } catch {
    return null;
  }
}

/**
 * A modal changelog, intentionally not a route. react-markdown does not parse
 * raw HTML without rehype-raw; `skipHtml` makes that invariant explicit.
 */
export default function ChangelogDialog({ open, onClose }: ChangelogDialogProps) {
  const [releases, setReleases] = useState<ChangelogRelease[]>([]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setReleases([]);
    setExpandedDates(new Set());
    setArchiveExpanded(false);

    fetchChangelog()
      .then(({ releases: loadedReleases }) => {
        if (cancelled) return;
        const orderedReleases = sortNewestFirst(loadedReleases);
        const { recent } = splitReleasesByAge(orderedReleases, new Date());
        setReleases(orderedReleases);
        setExpandedDates(recent[0] ? new Set([recent[0].date]) : new Set());
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, retryCount]);

  const retry = useCallback(() => setRetryCount((count) => count + 1), []);
  const { recent, archive } = useMemo(
    () => splitReleasesByAge(releases, new Date()),
    [releases]
  );
  const toggleRelease = useCallback((date: string, isExpanded: boolean) => {
    setExpandedDates((dates) => {
      const nextDates = new Set(dates);
      if (isExpanded) nextDates.add(date);
      else nextDates.delete(date);
      return nextDates;
    });
  }, []);

  const renderRelease = (release: ChangelogRelease) => (
    <Accordion
      key={release.date}
      expanded={expandedDates.has(release.date)}
      onChange={(_, isExpanded) => toggleRelease(release.date, isExpanded)}
      disableGutters
      elevation={0}
      sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.25, sm: 1.5 }, alignItems: { sm: 'baseline' } }}>
          <Typography component="span" variant="subtitle2" color="text.secondary">
            {formatReleaseDate(release.date)}
          </Typography>
          <Typography component="span" variant="subtitle1" fontWeight={600}>
            {release.title}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box
          sx={{
            '& > *:first-of-type': { mt: 0 },
            '& > *:last-child': { mb: 0 },
            '& pre': { overflowX: 'auto', p: 1.5, bgcolor: 'action.hover', borderRadius: 1 },
            '& code': { fontFamily: 'monospace' },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            skipHtml
            components={{
              a: ({ href, children }) => {
                const safeHref = safeExternalHref(href);
                return safeHref ? (
                  <a href={safeHref} target="_blank" rel="noopener noreferrer">{children}</a>
                ) : <>{children}</>;
              },
            }}
          >
            {release.markdown}
          </ReactMarkdown>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="whats-new-dialog-title"
      fullWidth
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        sx: {
          alignSelf: 'flex-start',
          mt: '78px',
          mb: 4,
          width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 64px)' },
          maxWidth: '1180px',
          maxHeight: 'calc(100vh - 110px)',
          '@media (min-width:600px)': {
            mt: '64px',
            maxHeight: 'calc(100vh - 96px)',
          },
        },
      }}
    >
      <DialogTitle id="whats-new-dialog-title" sx={{ pr: 7 }}>
        What's New
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        data-testid="changelog-dialog-content"
        dividers
        sx={{ minHeight: 180, minWidth: 0, overflowY: 'auto' }}
      >
        {loading && (
          <Box
            role="status"
            aria-live="polite"
            sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <CircularProgress aria-label="Loading updates" size={28} />
          </Box>
        )}

        {!loading && failed && (
          <Box role="status">
            <Alert severity="error" sx={{ mb: 2 }}>
              We couldn't load updates right now. Please try again.
            </Alert>
            <Button variant="outlined" onClick={retry}>Try again</Button>
          </Box>
        )}

        {!loading && !failed && releases.length === 0 && (
          <Typography color="text.secondary">No updates have been published yet.</Typography>
        )}

        {!loading && !failed && releases.length > 0 && (
          <Box component="section" aria-labelledby="recent-updates-heading">
            <Typography id="recent-updates-heading" component="h2" variant="overline" color="text.secondary">
              Recent updates
            </Typography>
            {recent.length > 0 ? recent.map(renderRelease) : (
              <Typography color="text.secondary">No recent updates.</Typography>
            )}
          </Box>
        )}

        {!loading && !failed && archive.length > 0 && (
          <Accordion
            expanded={archiveExpanded}
            onChange={(_, isExpanded) => setArchiveExpanded(isExpanded)}
            TransitionProps={{ unmountOnExit: true }}
            disableGutters
            elevation={0}
            sx={{ mt: 2, '&:before': { display: 'none' }, borderTop: 1, borderColor: 'divider' }}
          >
            <AccordionSummary aria-label="Older" expandIcon={<ExpandMoreIcon />}>
              <Typography component="span" variant="subtitle2">Older</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {archive.map(renderRelease)}
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
    </Dialog>
  );
}
